/**
 * Local visual repair for a selected PDF paint rectangle. The input is a
 * rasterized copy ONLY used to make a tiny correction patch; the PDF's source
 * page is never replaced or rasterized on export.
 *
 * Flat backgrounds: replace with the median background color. This avoids
 * dragging blurred source pixels or a white box across a dark title page.
 * Patterned / composite backgrounds: NO guessed interpolation; fail closed.
 * Actual deletion of PDF content requires a backend editor/redaction engine.
 */
function median(values) {
  if (!values.length) return 255;
  const sorted = [...values].sort((a,b)=>a-b);
  return sorted[Math.floor(sorted.length/2)];
}
const pixel = (data, width, x, y) => {
  const i=(y*width+x)*4;
  return [data[i],data[i+1],data[i+2]];
};
export function backgroundEstimate(data,width,height,rect) {
  const {x0,y0,x1,y1}=rect;
  const perimeter=[];
  const stride=Math.max(1,Math.floor(Math.max(x1-x0,y1-y0)/60));
  const left=Math.max(0,x0-2),right=Math.min(width-1,x1+2);
  const top=Math.max(0,y0-2),bottom=Math.min(height-1,y1+2);
  for(let x=left;x<=right;x+=stride){
    perimeter.push(pixel(data,width,x,top),pixel(data,width,x,bottom));
  }
  for(let y=top;y<=bottom;y+=stride){
    perimeter.push(pixel(data,width,left,y),pixel(data,width,right,y));
  }
  if (!perimeter.length) return {color:[255,255,255],flat:true,spread:0};
  const color=[0,1,2].map(channel=>median(perimeter.map(p=>p[channel])));
  const deviations=perimeter.map(p=>Math.max(...p.map((v,i)=>Math.abs(v-color[i])))).sort((a,b)=>a-b);
  const spread=deviations[Math.floor((deviations.length-1)*0.8)];
  return {color,flat:spread<=19,spread};
}
// Newly added text/images have no original PDF paint rectangle. Never try to
// repair their background, and reject malformed coordinates before drawing.
// The console marker helps confirm CRA/Webpack is serving the FIXED module,
// rather than an older bundle or a duplicate PdfStudio folder.
export const PDF_STUDIO_REPAIR_VERSION = '1.2.2';
if (typeof console !== 'undefined') {
  console.info('[PDF Studio] Metin düzenleme koruması v1.2.2 aktif');
}

export function hasValidOriginalRegion(region) {
  return !!region && typeof region === 'object' &&
    ['x', 'y', 'width', 'height'].every(key => Number.isFinite(region[key])) &&
    region.width > 0 && region.height > 0;
}

/**
 * Only text/image objects extracted from the *original PDF* can need a
 * source-background repair. Newly inserted overlays never qualify, even while
 * their text draft differs from their committed text value.
 */
export function needsPreviewSourceRepair(item, textDraft, needsSourceReplacement = false) {
  if (!hasValidOriginalRegion(item?.originalRegion)) return false;
  const draftChanged = textDraft?.id === item.id && textDraft.text !== item.text;
  return !!needsSourceReplacement || draftChanged;
}

export function repairRegion(ctx,region,sx,sy) {
  if (!ctx?.canvas || !hasValidOriginalRegion(region) ||
      !Number.isFinite(sx) || !Number.isFinite(sy) || sx <= 0 || sy <= 0) {
    return {flat:false,spread:255,safe:false,reason:'invalid-region'};
  }
  const canvas=ctx.canvas;
  const x0=Math.max(0,Math.floor(region.x*sx));
  const y0=Math.max(0,Math.floor(region.y*sy));
  const x1=Math.min(canvas.width,Math.ceil((region.x+region.width)*sx));
  const y1=Math.min(canvas.height,Math.ceil((region.y+region.height)*sy));
  if(x1<=x0 || y1<=y0) return {flat:false,spread:255,safe:false};
  // Restrict pixel access to this small rectangle plus a two-pixel border.
  // Reading an entire 4K/A3 canvas on every mousemove caused expensive copies.
  const localX=Math.max(0,x0-2),localY=Math.max(0,y0-2);
  const localRight=Math.min(canvas.width,x1+2),localBottom=Math.min(canvas.height,y1+2);
  const source=ctx.getImageData(localX,localY,localRight-localX,localBottom-localY);
  const localRect={x0:x0-localX,y0:y0-localY,x1:x1-localX,y1:y1-localY};
  const estimate=backgroundEstimate(source.data,source.width,source.height,localRect);
  if(estimate.flat){
    ctx.fillStyle=`rgb(${estimate.color.join(',')})`;
    ctx.fillRect(x0,y0,x1-x0,y1-y0);
    return {...estimate,safe:true};
  }
  // Never interpolate a patterned area. Interpolation was destroying badges,
  // circuit lines and layered icons in Evil Corp (page 3). The renderer keeps
  // the pristine original visible; exporting raises an explicit error instead.
  return {...estimate, safe:false};
}
